import React from 'react';
import { Link } from 'wouter';
import { 
  ChevronLeft, 
  Lightbulb, 
  AlertTriangle, 
  Info, 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle,
  Clock,
  Share2,
  BookOpen,
  FileText,
  Book
} from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from '@/components/ui/badge';
import Slideshow, { SlideContent } from '@/components/lectures/Slideshow';

// Simple KeyTakeaway component
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Lightbulb className="text-blue-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-blue-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Clinical Note component
const ClinicalNote = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <AlertTriangle className="text-amber-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-amber-800 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Evidence Box component
const EvidenceBox = ({ title, children }: { title: string, children: React.ReactNode }) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-5 my-6">
      <div className="flex items-center mb-2">
        <Info className="text-gray-600 h-5 w-5 mr-2" />
        <h4 className="font-semibold text-gray-900">{title}</h4>
      </div>
      <div className="text-gray-700 text-sm">
        {children}
      </div>
    </div>
  );
};

// Insulin Signaling Pathway Diagram
const InsulinSignalingDiagram = () => (
  <svg width="100%" height="100%" viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f8fafc" />
        <stop offset="100%" stopColor="#f1f5f9" />
      </linearGradient>
      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.1"/>
      </filter>
      <linearGradient id="cellMembrane" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#cbd5e1" />
        <stop offset="100%" stopColor="#94a3b8" />
      </linearGradient>
      <linearGradient id="cellGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#e0f2fe" />
        <stop offset="100%" stopColor="#bae6fd" />
      </linearGradient>
    </defs>
    
    {/* Background */}
    <rect width="800" height="600" fill="url(#bgGradient)" rx="5" ry="5" />
    
    {/* Title */}
    <text x="400" y="40" textAnchor="middle" fontSize="20" fontWeight="bold" fontFamily="Georgia, serif">
      Insulin Signaling Pathway
    </text>
    <text x="400" y="65" textAnchor="middle" fontSize="14" fontFamily="Georgia, serif" fill="#666">
      Normal vs. Insulin Resistant State
    </text>
    
    {/* Cell membrane */}
    <rect x="100" y="150" width="600" height="30" fill="url(#cellMembrane)" rx="2" />
    <text x="400" y="170" textAnchor="middle" fontSize="12" fill="white" fontWeight="bold">Cell Membrane</text>
    
    {/* Normal state - left side */}
    <text x="200" y="120" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#0369a1">Normal State</text>
    
    {/* Insulin and receptor - normal */}
    <circle cx="200" cy="130" r="15" fill="#3b82f6" filter="url(#shadow)" />
    <text x="200" y="134" textAnchor="middle" fontSize="10" fill="white" fontWeight="bold">INS</text>
    
    <path d="M 180,150 L 180,180 L 220,180 L 220,150" fill="none" stroke="#0c4a6e" strokeWidth="2" />
    <text x="200" y="200" textAnchor="middle" fontSize="10">Insulin Receptor</text>
    
    {/* IRS-1 */}
    <rect x="180" y="220" width="40" height="20" rx="3" fill="#0ea5e9" filter="url(#shadow)" />
    <text x="200" y="233" textAnchor="middle" fontSize="10" fill="white">IRS-1</text>
    
    {/* PI3K */}
    <rect x="180" y="270" width="40" height="20" rx="3" fill="#0284c7" filter="url(#shadow)" />
    <text x="200" y="283" textAnchor="middle" fontSize="10" fill="white">PI3K</text>
    
    {/* AKT */}
    <rect x="180" y="320" width="40" height="20" rx="3" fill="#0369a1" filter="url(#shadow)" />
    <text x="200" y="333" textAnchor="middle" fontSize="10" fill="white">AKT</text>
    
    {/* GSK3 and AS160 */}
    <rect x="140" y="370" width="40" height="20" rx="3" fill="#075985" filter="url(#shadow)" />
    <text x="160" y="383" textAnchor="middle" fontSize="10" fill="white">GSK3</text>
    
    <rect x="220" y="370" width="40" height="20" rx="3" fill="#075985" filter="url(#shadow)" />
    <text x="240" y="383" textAnchor="middle" fontSize="10" fill="white">AS160</text>
    
    {/* Glycogen and GLUT4 */}
    <circle cx="160" cy="430" r="25" fill="#bae6fd" stroke="#0c4a6e" strokeWidth="1" />
    <text x="160" y="433" textAnchor="middle" fontSize="10">Glycogen</text>
    
    <rect x="215" y="420" width="50" height="30" rx="5" fill="#bae6fd" stroke="#0c4a6e" strokeWidth="1" />
    <text x="240" y="433" textAnchor="middle" fontSize="10">GLUT4</text>
    <text x="240" y="445" textAnchor="middle" fontSize="8">Translocation</text>
    
    {/* Glucose */}
    <circle cx="240" cy="480" r="15" fill="#fde68a" filter="url(#shadow)" />
    <text x="240" y="484" textAnchor="middle" fontSize="8" fill="#78350f">Glucose</text>
    
    {/* Connection lines - normal pathway */}
    <line x1="200" y1="180" x2="200" y2="220" stroke="#0ea5e9" strokeWidth="2" />
    <line x1="200" y1="240" x2="200" y2="270" stroke="#0ea5e9" strokeWidth="2" />
    <line x1="200" y1="290" x2="200" y2="320" stroke="#0ea5e9" strokeWidth="2" />
    <line x1="180" y1="330" x2="160" y2="370" stroke="#0ea5e9" strokeWidth="2" />
    <line x1="220" y1="330" x2="240" y2="370" stroke="#0ea5e9" strokeWidth="2" />
    <line x1="160" y1="390" x2="160" y2="405" stroke="#0ea5e9" strokeWidth="2" />
    <line x1="240" y1="390" x2="240" y2="420" stroke="#0ea5e9" strokeWidth="2" />
    <line x1="240" y1="450" x2="240" y2="465" stroke="#0ea5e9" strokeWidth="2" />
    
    {/* Result label - normal */}
    <text x="200" y="520" textAnchor="middle" fontSize="12" fill="#0369a1" fontWeight="bold">
      Efficient Glucose Uptake
    </text>
    <text x="200" y="540" textAnchor="middle" fontSize="12" fill="#0369a1">
      and Metabolism
    </text>
    
    {/* Insulin resistant state - right side */}
    <text x="600" y="120" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#b91c1c">Insulin Resistant State</text>
    
    {/* Insulin and receptor - resistant */}
    <circle cx="600" cy="130" r="15" fill="#3b82f6" filter="url(#shadow)" />
    <text x="600" y="134" textAnchor="middle" fontSize="10" fill="white" fontWeight="bold">INS</text>
    
    <path d="M 580,150 L 580,180 L 620,180 L 620,150" fill="none" stroke="#450a0a" strokeWidth="2" />
    <text x="600" y="200" textAnchor="middle" fontSize="10">Insulin Receptor</text>
    
    {/* Inflammatory factors */}
    <circle cx="540" cy="200" r="20" fill="#fca5a5" filter="url(#shadow)" />
    <text x="540" y="197" textAnchor="middle" fontSize="8" fill="#7f1d1d">TNF-α</text>
    <text x="540" y="207" textAnchor="middle" fontSize="8" fill="#7f1d1d">IL-6</text>
    
    {/* FFA and lipids */}
    <path d="M 650,200 Q 670,180 660,220 Q 650,240 640,220 Q 630,200 650,200" fill="#fee2e2" stroke="#7f1d1d" strokeWidth="1" />
    <text x="650" y="210" textAnchor="middle" fontSize="8" fill="#7f1d1d">FFA</text>
    
    {/* IRS-1 with serine phosphorylation */}
    <rect x="580" y="220" width="40" height="20" rx="3" fill="#ef4444" filter="url(#shadow)" />
    <text x="600" y="233" textAnchor="middle" fontSize="10" fill="white">IRS-1</text>
    <circle cx="585" cy="225" r="5" fill="#fecaca" stroke="#7f1d1d" strokeWidth="1" />
    <text x="585" y="228" textAnchor="middle" fontSize="6" fill="#7f1d1d">P</text>
    
    {/* PI3K - reduced activation */}
    <rect x="580" y="270" width="40" height="20" rx="3" fill="#f87171" stroke="#7f1d1d" strokeDasharray="2" filter="url(#shadow)" />
    <text x="600" y="283" textAnchor="middle" fontSize="10" fill="white">PI3K</text>
    
    {/* AKT - reduced activation */}
    <rect x="580" y="320" width="40" height="20" rx="3" fill="#f87171" stroke="#7f1d1d" strokeDasharray="2" filter="url(#shadow)" />
    <text x="600" y="333" textAnchor="middle" fontSize="10" fill="white">AKT</text>
    
    {/* ER Stress and ROS */}
    <ellipse cx="680" cy="320" rx="30" ry="20" fill="#fed7aa" stroke="#9a3412" strokeWidth="1" />
    <text x="680" y="315" textAnchor="middle" fontSize="8" fill="#9a3412">ER</text>
    <text x="680" y="325" textAnchor="middle" fontSize="8" fill="#9a3412">Stress</text>
    
    <ellipse cx="520" cy="320" rx="25" ry="15" fill="#fef9c3" stroke="#713f12" strokeWidth="1" />
    <text x="520" y="323" textAnchor="middle" fontSize="8" fill="#713f12">ROS</text>
    
    {/* GSK3 and AS160 - reduced inhibition */}
    <rect x="540" y="370" width="40" height="20" rx="3" fill="#f87171" filter="url(#shadow)" />
    <text x="560" y="383" textAnchor="middle" fontSize="10" fill="white">GSK3</text>
    
    <rect x="620" y="370" width="40" height="20" rx="3" fill="#f87171" filter="url(#shadow)" />
    <text x="640" y="383" textAnchor="middle" fontSize="10" fill="white">AS160</text>
    
    {/* Glycogen and GLUT4 - impaired */}
    <circle cx="560" cy="430" r="25" fill="#fecaca" stroke="#7f1d1d" strokeWidth="1" strokeDasharray="3" />
    <text x="560" y="433" textAnchor="middle" fontSize="10">Glycogen</text>
    <text x="560" y="445" textAnchor="middle" fontSize="8">↓ Synthesis</text>
    
    <rect x="615" y="420" width="50" height="30" rx="5" fill="#fecaca" stroke="#7f1d1d" strokeWidth="1" strokeDasharray="3" />
    <text x="640" y="433" textAnchor="middle" fontSize="10">GLUT4</text>
    <text x="640" y="445" textAnchor="middle" fontSize="8">↓ Translocation</text>
    
    {/* Glucose */}
    <circle cx="640" cy="480" r="15" fill="#fde68a" filter="url(#shadow)" />
    <text x="640" y="484" textAnchor="middle" fontSize="8" fill="#78350f">Glucose</text>
    
    {/* Connection lines - impaired pathway */}
    <line x1="600" y1="180" x2="600" y2="220" stroke="#ef4444" strokeWidth="1" strokeDasharray="3" />
    <line x1="540" y1="220" x2="580" y2="220" stroke="#ef4444" strokeWidth="1" />
    <line x1="650" y1="220" x2="620" y2="220" stroke="#ef4444" strokeWidth="1" />
    <line x1="600" y1="240" x2="600" y2="270" stroke="#ef4444" strokeWidth="1" strokeDasharray="3" />
    <line x1="600" y1="290" x2="600" y2="320" stroke="#ef4444" strokeWidth="1" strokeDasharray="3" />
    <line x1="580" y1="330" x2="560" y2="370" stroke="#ef4444" strokeWidth="1" strokeDasharray="3" />
    <line x1="620" y1="330" x2="640" y2="370" stroke="#ef4444" strokeWidth="1" strokeDasharray="3" />
    <line x1="560" y1="390" x2="560" y2="405" stroke="#ef4444" strokeWidth="1" strokeDasharray="3" />
    <line x1="640" y1="390" x2="640" y2="420" stroke="#ef4444" strokeWidth="1" strokeDasharray="3" />
    <line x1="640" y1="450" x2="640" y2="465" stroke="#ef4444" strokeWidth="1" strokeDasharray="3" />
    
    {/* Mitochondrial dysfunction */}
    <ellipse cx="700" cy="430" rx="30" ry="20" fill="#fed7aa" stroke="#9a3412" strokeWidth="1" />
    <text x="700" y="425" textAnchor="middle" fontSize="8" fill="#9a3412">Mitochondrial</text>
    <text x="700" y="435" textAnchor="middle" fontSize="8" fill="#9a3412">Dysfunction</text>
    
    {/* Result label - impaired */}
    <text x="600" y="520" textAnchor="middle" fontSize="12" fill="#b91c1c" fontWeight="bold">
      Impaired Glucose Uptake
    </text>
    <text x="600" y="540" textAnchor="middle" fontSize="12" fill="#b91c1c">
      and Metabolism
    </text>
  </svg>
);

// Function to create a video placeholder (can be replaced with actual video later)
const VideoPlaceholder = () => {
  return (
    <div className="mt-8 border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-100 p-4 border-b flex justify-between items-center">
        <h3 className="font-medium">Lecture Video</h3>
        <span className="text-sm text-gray-500">45:00</span>
      </div>
      <div className="aspect-video bg-gray-200">
        <iframe 
          src="https://share.descript.com/embed/Rb3EURXn2GL" 
          width="100%" 
          height="100%" 
          frameBorder="0" 
          allowFullScreen
          className="w-full h-full"
        ></iframe>
      </div>
    </div>
  );
};

// Create lecture slides content
const lectureSlides: SlideContent[] = [
  // Slide 1: Introduction
  {
    title: "Insulin Resistance: Molecular Pathways & Clinical Interventions",
    content: (
      <>
        <p className="mb-4">
          Insulin resistance (IR) lies at the confluence of multiple age-related metabolic disorders, including type 2 diabetes, cardiovascular disease, non-alcoholic fatty liver disease, and neurodegenerative conditions. While traditionally viewed through the narrow lens of impaired cellular glucose uptake, contemporary research reveals insulin resistance as a systems-level disorder involving complex, interconnected pathways.
        </p>
        
        <KeyTakeaway>
          <p>Insulin resistance is not simply a failure of glucose uptake but a complex adaptive response involving multiple organ systems and cellular pathways. This systems perspective is critical for developing effective clinical interventions.</p>
        </KeyTakeaway>
        
        <p className="mt-4">
          This lecture explores the mechanisms that drive insulin resistance at the cellular and molecular level, their relationships with aging, and translates this understanding into evidence-based, precision-medicine approaches for clinical practice.
        </p>
      </>
    )
  },
  
  // Slide 2: Core Mechanisms - Insulin Signaling
  {
    title: "Core Mechanisms: The Insulin Signaling Cascade",
    content: (
      <>
        <p className="mb-4">
          Insulin signaling begins when insulin binds to its receptor, triggering a precise sequence of phosphorylation events that ultimately promotes glucose uptake, glycogen synthesis, protein synthesis, and inhibition of lipolysis and gluconeogenesis.
        </p>
        
        <div className="my-8 flex justify-center">
          <InsulinSignalingDiagram />
        </div>
        
        <p className="mt-4">
          In normal signaling (left), insulin binding initiates IRS phosphorylation, activating PI3K and Akt to enhance GLUT4 translocation and stimulate glycogen synthesis through GSK3 inhibition.
        </p>
        <p className="mt-2">
          During insulin resistance (right), multiple factors disturb this cascade:
        </p>
        <ul className="list-disc ml-6 mt-2">
          <li>Pro-inflammatory cytokines promote inhibitory serine phosphorylation of IRS</li>
          <li>Free fatty acids activate inflammatory pathways</li>
          <li>ER stress and ROS impair insulin signaling</li>
          <li>Mitochondrial dysfunction reduces metabolic flexibility</li>
        </ul>
      </>
    )
  },
  
  // Slide 3: Mitochondrial Dysfunction
  {
    title: "Core Mechanisms: Mitochondrial Dysfunction",
    content: (
      <>
        <p className="mb-4">
          Mitochondria are not merely passive victims in insulin resistance but active contributors to its pathogenesis. Several mechanisms link mitochondrial health to insulin sensitivity:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-md">
            <h3 className="font-medium text-blue-900 mb-2">Defective Fatty Acid Metabolism</h3>
            <p><strong>Incomplete fatty acid oxidation</strong> leads to accumulation of acylcarnitines and other intermediates that interfere with insulin signaling</p>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-md">
            <h3 className="font-medium text-blue-900 mb-2">Oxidative Stress</h3>
            <p><strong>Excessive ROS production</strong> activates stress-responsive kinases like JNK and IKKβ that inhibit insulin signaling</p>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-md">
            <h3 className="font-medium text-blue-900 mb-2">Reduced Mitochondrial Dynamics</h3>
            <p><strong>Impaired fission/fusion</strong> and reduced mitochondrial content decrease metabolic flexibility and ATP production</p>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-md">
            <h3 className="font-medium text-blue-900 mb-2">Biogenesis Defects</h3>
            <p><strong>Impaired mitochondrial biogenesis</strong> through decreased PGC-1α activity in insulin-resistant states</p>
          </div>
        </div>
        
        <EvidenceBox title="Research Highlight">
          <p>
            Petersen et al. demonstrated using non-invasive 13C/31P magnetic resonance spectroscopy that insulin-resistant offspring of patients with type 2 diabetes exhibit a 30% reduction in mitochondrial oxidative phosphorylation, which precedes detectable changes in insulin sensitivity.
          </p>
        </EvidenceBox>
      </>
    )
  },
  
  // Slide 4: Chronic Inflammation
  {
    title: "Core Mechanisms: Chronic Inflammation",
    content: (
      <>
        <p className="mb-4">
          Low-grade chronic inflammation represents a key driver of insulin resistance. This "metaflammation" differs from classical inflammation by being metabolically-induced, chronic, and low-grade.
        </p>
        
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
          <h3 className="font-semibold text-amber-900 mb-3">Key Inflammatory Processes in Insulin Resistance</h3>
          
          <div className="space-y-4">
            <div className="flex">
              <div className="flex-shrink-0 mt-1 mr-3">
                <div className="w-6 h-6 rounded-full bg-amber-200 flex items-center justify-center">
                  <span className="text-amber-800 font-medium">1</span>
                </div>
              </div>
              <div>
                <p className="font-medium text-amber-900">Adipose Tissue Macrophage Polarization</p>
                <p className="text-sm text-amber-800">Shift from anti-inflammatory M2 to pro-inflammatory M1 phenotype during obesity</p>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mt-1 mr-3">
                <div className="w-6 h-6 rounded-full bg-amber-200 flex items-center justify-center">
                  <span className="text-amber-800 font-medium">2</span>
                </div>
              </div>
              <div>
                <p className="font-medium text-amber-900">Pro-inflammatory Cytokine Release</p>
                <p className="text-sm text-amber-800">TNF-α, IL-1β, and IL-6 directly interfere with insulin signaling</p>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mt-1 mr-3">
                <div className="w-6 h-6 rounded-full bg-amber-200 flex items-center justify-center">
                  <span className="text-amber-800 font-medium">3</span>
                </div>
              </div>
              <div>
                <p className="font-medium text-amber-900">Activation of Inflammatory Pathways</p>
                <p className="text-sm text-amber-800">JNK and NF-κB activation leading to inhibitory serine phosphorylation of IRS proteins</p>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mt-1 mr-3">
                <div className="w-6 h-6 rounded-full bg-amber-200 flex items-center justify-center">
                  <span className="text-amber-800 font-medium">4</span>
                </div>
              </div>
              <div>
                <p className="font-medium text-amber-900">Receptor Expression Suppression</p>
                <p className="text-sm text-amber-800">Downregulation of insulin receptor expression by inflammatory signals</p>
              </div>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          <p>When assessing insulin resistance, biomarkers beyond fasting glucose and insulin are necessary. Consider measuring inflammatory markers (hsCRP, IL-6), oxidative stress markers, and adipokines for a comprehensive evaluation.</p>
        </ClinicalNote>
      </>
    )
  },
  
  // Slide 5: Nutrient Sensing Pathway Dysregulation
  {
    title: "Core Mechanisms: Nutrient Sensing Pathway Dysregulation",
    content: (
      <>
        <p className="mb-4">
          Insulin resistance involves disruptions in cellular nutrient sensing mechanisms, particularly mTOR (mechanistic target of rapamycin) and AMPK (AMP-activated protein kinase) signaling.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-purple-100 px-4 py-2">
              <h3 className="font-medium text-purple-900">mTOR Dysregulation</h3>
            </div>
            <div className="p-4">
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center mr-2 mt-0.5">
                    <Info className="h-3 w-3 text-purple-600" />
                  </div>
                  <span className="text-sm">Chronic mTORC1 activation from nutrient excess</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center mr-2 mt-0.5">
                    <Info className="h-3 w-3 text-purple-600" />
                  </div>
                  <span className="text-sm">Negative feedback inhibition of insulin signaling</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center mr-2 mt-0.5">
                    <Info className="h-3 w-3 text-purple-600" />
                  </div>
                  <span className="text-sm">Impaired autophagy and cellular maintenance</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-teal-100 px-4 py-2">
              <h3 className="font-medium text-teal-900">AMPK Dysregulation</h3>
            </div>
            <div className="p-4">
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-teal-100 flex items-center justify-center mr-2 mt-0.5">
                    <Info className="h-3 w-3 text-teal-600" />
                  </div>
                  <span className="text-sm">Decreased AMPK activity in obese/insulin-resistant states</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-teal-100 flex items-center justify-center mr-2 mt-0.5">
                    <Info className="h-3 w-3 text-teal-600" />
                  </div>
                  <span className="text-sm">Reduced fatty acid oxidation and mitochondrial function</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-teal-100 flex items-center justify-center mr-2 mt-0.5">
                    <Info className="h-3 w-3 text-teal-600" />
                  </div>
                  <span className="text-sm">Impaired cellular energy sensing mechanisms</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <KeyTakeaway>
          <p>The dysregulation of nutrient sensing pathways links overnutrition to insulin resistance. Therapeutic strategies that normalize these pathways—including intermittent fasting, time-restricted eating, and exercise—can effectively improve insulin sensitivity by recalibrating cellular energy sensing.</p>
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 6: Cross-Talk Between Pathways
  {
    title: "Pathway Cross-Talk in Insulin Resistance",
    content: (
      <>
        <p className="mb-6">
          The mechanisms driving insulin resistance do not operate in isolation but form an interconnected network. This explains why multimodal interventions are typically more effective than single-target approaches.
        </p>
        
        <div className="mb-6">
          <h3 className="font-semibold mb-3">Key Pathway Interactions</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-red-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Mitochondria &amp; Inflammation</h4>
              <p className="text-sm">Mitochondrial dysfunction increases ROS, triggering inflammatory pathways; pro-inflammatory cytokines impair mitochondrial function</p>
            </div>
            
            <div className="bg-gradient-to-br from-red-50 to-orange-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">ER Stress &amp; Inflammation</h4>
              <p className="text-sm">ER stress activates inflammatory pathways through the unfolded protein response; inflammatory cytokines can induce ER stress</p>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Lipotoxicity &amp; Mitochondria</h4>
              <p className="text-sm">Excess lipids impair mitochondrial biogenesis and function; mitochondrial dysfunction reduces capacity for fatty acid oxidation</p>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          <p>This interconnected nature of insulin resistance mechanisms explains why multimodal interventions targeting several pathways simultaneously (e.g., combining exercise, anti-inflammatory nutrition, and stress reduction) are more effective than single-target approaches.</p>
        </ClinicalNote>
      </>
    )
  },
  
  // Slide 7: Insulin Resistance and Aging
  {
    title: "Insulin Resistance and Aging: A Bidirectional Relationship",
    content: (
      <>
        <p className="mb-4">
          Insulin resistance and aging share a bidirectional relationship. Age-related changes promote insulin resistance, while insulin resistance accelerates multiple aspects of the aging process.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="font-semibold text-indigo-900 mb-3">How Aging Promotes Insulin Resistance</h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-indigo-600 text-xs font-medium">1</span>
                </div>
                <span className="text-sm"><strong>Increased visceral adiposity</strong> with redistribution of fat from subcutaneous to visceral compartments</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-indigo-600 text-xs font-medium">2</span>
                </div>
                <span className="text-sm"><strong>Sarcopenia and reduced physical activity</strong> decreasing muscle-mediated glucose disposal</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-indigo-600 text-xs font-medium">3</span>
                </div>
                <span className="text-sm"><strong>Age-related mitochondrial dysfunction</strong> with decreased oxidative capacity</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-indigo-600 text-xs font-medium">4</span>
                </div>
                <span className="text-sm"><strong>Chronic inflammation (inflammaging)</strong> with elevated pro-inflammatory cytokines</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-indigo-600 text-xs font-medium">5</span>
                </div>
                <span className="text-sm"><strong>Cellular senescence</strong> with accumulation of senescent cells secreting pro-inflammatory factors</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold text-rose-900 mb-3">How Insulin Resistance Accelerates Aging</h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-rose-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-rose-600 text-xs font-medium">1</span>
                </div>
                <span className="text-sm"><strong>Impaired autophagy</strong> reducing cellular quality control</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-rose-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-rose-600 text-xs font-medium">2</span>
                </div>
                <span className="text-sm"><strong>Increased oxidative stress</strong> causing macromolecular damage</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-rose-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-rose-600 text-xs font-medium">3</span>
                </div>
                <span className="text-sm"><strong>Enhanced glycation</strong> leading to AGE formation and tissue dysfunction</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-rose-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-rose-600 text-xs font-medium">4</span>
                </div>
                <span className="text-sm"><strong>Disrupted circadian rhythms</strong> impacting metabolic homeostasis</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-rose-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-rose-600 text-xs font-medium">5</span>
                </div>
                <span className="text-sm"><strong>Neurodegeneration</strong> through reduced brain insulin signaling and increased inflammation</span>
              </li>
            </ul>
          </div>
        </div>
        
        <EvidenceBox title="Research Highlight">
          <p>
            Insulin-resistant individuals show accelerated epigenetic aging compared to insulin-sensitive counterparts matched for chronological age, BMI, and sex. DNA methylation patterns in insulin resistance resemble those seen in advanced age, suggesting shared molecular pathways.
          </p>
        </EvidenceBox>
      </>
    )
  },
  
  // Slide 8: Comprehensive Assessment
  {
    title: "Precision Medicine: Comprehensive Assessment",
    content: (
      <>
        <p className="mb-4">
          The complex, multifactorial nature of insulin resistance necessitates a personalized medicine approach that addresses the dominant mechanisms in individual patients.
        </p>
        
        <h3 className="font-semibold mb-3">Assessment Beyond HOMA-IR</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <h4 className="font-medium text-blue-900 mb-2">Glucose Dynamics</h4>
            <ul className="space-y-1">
              <li className="text-sm">• Fasting glucose & insulin</li>
              <li className="text-sm">• HOMA-IR, QUICKI</li>
              <li className="text-sm">• Postprandial glucose/insulin</li>
              <li className="text-sm">• HbA1c, 1,5-anhydroglucitol</li>
              <li className="text-sm">• Continuous glucose monitoring</li>
            </ul>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg border border-red-100">
            <h4 className="font-medium text-red-900 mb-2">Inflammation Markers</h4>
            <ul className="space-y-1">
              <li className="text-sm">• hsCRP</li>
              <li className="text-sm">• IL-6, TNF-α</li>
              <li className="text-sm">• White blood cell differential</li>
              <li className="text-sm">• Neutrophil:lymphocyte ratio</li>
              <li className="text-sm">• Ferritin</li>
            </ul>
          </div>
          
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
            <h4 className="font-medium text-amber-900 mb-2">Oxidative Stress</h4>
            <ul className="space-y-1">
              <li className="text-sm">• F2-isoprostanes</li>
              <li className="text-sm">• Malondialdehyde</li>
              <li className="text-sm">• 8-OHdG</li>
              <li className="text-sm">• Oxidized LDL</li>
              <li className="text-sm">• Glutathione status</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-100">
            <h4 className="font-medium text-green-900 mb-2">Adipokines</h4>
            <ul className="space-y-1">
              <li className="text-sm">• Leptin</li>
              <li className="text-sm">• Adiponectin</li>
              <li className="text-sm">• Leptin:adiponectin ratio</li>
              <li className="text-sm">• Resistin</li>
              <li className="text-sm">• Visfatin</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
            <h4 className="font-medium text-purple-900 mb-2">Mitochondrial Health</h4>
            <ul className="space-y-1">
              <li className="text-sm">• Lactate</li>
              <li className="text-sm">• Pyruvate</li>
              <li className="text-sm">• Lactate:pyruvate ratio</li>
              <li className="text-sm">• Organic acids</li>
              <li className="text-sm">• Acylcarnitine profile</li>
            </ul>
          </div>
          
          <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
            <h4 className="font-medium text-indigo-900 mb-2">Body Composition</h4>
            <ul className="space-y-1">
              <li className="text-sm">• Visceral fat (DEXA/MRI)</li>
              <li className="text-sm">• Muscle mass</li>
              <li className="text-sm">• Intramuscular fat</li>
              <li className="text-sm">• Waist:hip ratio</li>
              <li className="text-sm">• Body fat percentage</li>
            </ul>
          </div>
        </div>
        
        <ClinicalNote>
          <p>The pattern of abnormalities across these biomarker categories can guide the selection of targeted therapeutic protocols based on dominant mechanisms.</p>
        </ClinicalNote>
      </>
    )
  },
  
  // Slide 9: Clinical Protocols
  {
    title: "Clinical Protocols: Mechanism-Based Therapeutic Strategies",
    content: (
      <>
        <h3 className="font-semibold mb-4">Protocol 1: Targeting Mitochondrial Function</h3>
        <div className="bg-blue-50 p-5 rounded-lg mb-6">
          <p className="italic text-blue-800 mb-3">For patients with evidence of mitochondrial dysfunction (elevated lactate, fatigue, poor exercise recovery)</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-3 rounded">
              <h4 className="font-medium text-blue-800 mb-2">Exercise Prescription</h4>
              <ul className="text-sm space-y-1">
                <li>• Combined resistance and HIIT</li>
                <li>• Progressive intensity</li>
                <li>• Focus on large muscle groups</li>
                <li>• 3-4 sessions/week</li>
              </ul>
            </div>
            
            <div className="bg-white p-3 rounded">
              <h4 className="font-medium text-blue-800 mb-2">Nutrient Support</h4>
              <ul className="text-sm space-y-1">
                <li>• CoQ10 (100-300mg daily)</li>
                <li>• L-carnitine (2g daily)</li>
                <li>• Ribose (5g daily)</li>
                <li>• PQQ (20mg daily)</li>
              </ul>
            </div>
            
            <div className="bg-white p-3 rounded">
              <h4 className="font-medium text-blue-800 mb-2">Lifestyle</h4>
              <ul className="text-sm space-y-1">
                <li>• Cold exposure</li>
                <li>• Sauna sessions</li>
                <li>• Stress management</li>
                <li>• Quality sleep optimization</li>
              </ul>
            </div>
          </div>
        </div>
        
        <h3 className="font-semibold mb-4">Protocol 2: Targeting Chronic Inflammation</h3>
        <div className="bg-red-50 p-5 rounded-lg mb-6">
          <p className="italic text-red-800 mb-3">For patients with elevated inflammatory markers (hsCRP &gt;1 mg/L, high IL-6)</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-3 rounded">
              <h4 className="font-medium text-red-800 mb-2">Dietary Intervention</h4>
              <ul className="text-sm space-y-1">
                <li>• Mediterranean or low-inflammatory diet</li>
                <li>• Emphasis on omega-3 rich foods</li>
                <li>• Polyphenol-rich foods</li>
                <li>• Minimal refined carbohydrates</li>
              </ul>
            </div>
            
            <div className="bg-white p-3 rounded">
              <h4 className="font-medium text-red-800 mb-2">Nutraceuticals</h4>
              <ul className="text-sm space-y-1">
                <li>• Omega-3 (3-4g EPA+DHA daily)</li>
                <li>• Curcumin (500-1000mg daily)</li>
                <li>• SPMs</li>
                <li>• Boswellia</li>
              </ul>
            </div>
            
            <div className="bg-white p-3 rounded">
              <h4 className="font-medium text-red-800 mb-2">Lifestyle</h4>
              <ul className="text-sm space-y-1">
                <li>• Stress management</li>
                <li>• Optimized sleep</li>
                <li>• Moderate exercise</li>
                <li>• Gut health optimization</li>
              </ul>
            </div>
          </div>
        </div>
        
        <h3 className="font-semibold mb-4">Protocol 3: Targeting Nutrient Sensing Dysregulation</h3>
        <div className="bg-green-50 p-5 rounded-lg">
          <p className="italic text-green-800 mb-3">For patients with signs of mTOR overactivation (excessive body fat despite adequate protein intake)</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-3 rounded">
              <h4 className="font-medium text-green-800 mb-2">Time-Restricted Eating</h4>
              <ul className="text-sm space-y-1">
                <li>• 16:8 or 14:10 feeding:fasting</li>
                <li>• Early TRE preferred</li>
                <li>• Gradually extend fasting window</li>
                <li>• Hydration during fasting</li>
              </ul>
            </div>
            
            <div className="bg-white p-3 rounded">
              <h4 className="font-medium text-green-800 mb-2">Nutraceuticals</h4>
              <ul className="text-sm space-y-1">
                <li>• Berberine (500mg 2-3x daily)</li>
                <li>• Resveratrol (100-500mg daily)</li>
                <li>• AMPK activators</li>
                <li>• Alpha-lipoic acid</li>
              </ul>
            </div>
            
            <div className="bg-white p-3 rounded">
              <h4 className="font-medium text-green-800 mb-2">Protein Cycling</h4>
              <ul className="text-sm space-y-1">
                <li>• Alternate higher protein days (1.6g/kg)</li>
                <li>• With lower protein days (0.8g/kg)</li>
                <li>• Match with exercise timing</li>
                <li>• Focus on leucine-rich sources</li>
              </ul>
            </div>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 10: Monitoring Response and Emerging Therapies
  {
    title: "Monitoring Response and Emerging Therapeutic Targets",
    content: (
      <>
        <div className="bg-gray-50 p-5 rounded-lg mb-6">
          <h3 className="font-semibold mb-3">Effective Monitoring Beyond Glucose Metrics</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Biomarkers to Track</h4>
              <ul className="space-y-1">
                <li className="text-sm">• HOMA-IR, postprandial glucose excursions</li>
                <li className="text-sm">• Inflammatory markers (hsCRP, IL-6)</li>
                <li className="text-sm">• Neutrophil:lymphocyte ratio</li>
                <li className="text-sm">• Adipokines (adiponectin, leptin ratio)</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Functional Assessments</h4>
              <ul className="space-y-1">
                <li className="text-sm">• VO2max, grip strength</li>
                <li className="text-sm">• DEXA/InBody metrics</li>
                <li className="text-sm">• Energy levels, cognitive function</li>
                <li className="text-sm">• Sleep quality</li>
              </ul>
            </div>
          </div>
        </div>
        
        <h3 className="font-semibold mb-3">Emerging Therapeutic Targets</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-b from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-100">
            <h4 className="font-medium text-orange-900 mb-2">Senolytic Therapies</h4>
            <p className="text-sm">Targeted elimination of senescent cells to reduce their pro-inflammatory secretions and metabolic effects</p>
          </div>
          
          <div className="bg-gradient-to-b from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-100">
            <h4 className="font-medium text-blue-900 mb-2">NAD+ Precursors</h4>
            <p className="text-sm">NR and NMN to enhance mitochondrial function, sirtuin activity, and cellular energy metabolism</p>
          </div>
          
          <div className="bg-gradient-to-b from-green-50 to-teal-50 p-4 rounded-lg border border-green-100">
            <h4 className="font-medium text-green-900 mb-2">Triple Receptor Agonists</h4>
            <p className="text-sm">GLP-1/GIP/glucagon triple receptor agonists as advanced incretin-based therapies</p>
          </div>
          
          <div className="bg-gradient-to-b from-violet-50 to-indigo-50 p-4 rounded-lg border border-violet-100">
            <h4 className="font-medium text-violet-900 mb-2">Microbiome Interventions</h4>
            <p className="text-sm">Postbiotics, precision probiotics, and microbiome-modulating compounds</p>
          </div>
          
          <div className="bg-gradient-to-b from-rose-50 to-pink-50 p-4 rounded-lg border border-rose-100">
            <h4 className="font-medium text-rose-900 mb-2">Adipokine Modulators</h4>
            <p className="text-sm">Targeting adiponectin, FGF21, and myokines to regulate metabolic signaling</p>
          </div>
          
          <div className="bg-gradient-to-b from-emerald-50 to-green-50 p-4 rounded-lg border border-emerald-100">
            <h4 className="font-medium text-emerald-900 mb-2">Mitochondrial Enhancers</h4>
            <p className="text-sm">Novel compounds targeting mitochondrial biogenesis, dynamics, and quality control</p>
          </div>
        </div>
        
        <KeyTakeaway>
          <p>The future of insulin resistance therapy lies in precise mechanism-based interventions rather than glucose-centric approaches. By identifying and targeting the specific pathways driving insulin resistance in each patient, clinicians can address root causes rather than downstream consequences.</p>
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 11: Conclusion
  {
    title: "Conclusion: Systems Approach to Insulin Resistance",
    content: (
      <>
        <p className="mb-4">
          Insulin resistance represents a complex systems disorder with diverse mechanistic drivers. Effective clinical management requires:
        </p>
        
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <ol className="space-y-4">
            <li className="flex">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-blue-800 font-medium">1</span>
              </div>
              <div>
                <p className="font-medium text-blue-900">Comprehensive assessment beyond simple glucose metrics</p>
                <p className="text-sm text-blue-800">Looking at inflammatory markers, mitochondrial function, and adipokines</p>
              </div>
            </li>
            
            <li className="flex">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-blue-800 font-medium">2</span>
              </div>
              <div>
                <p className="font-medium text-blue-900">Identification of predominant pathophysiological mechanisms</p>
                <p className="text-sm text-blue-800">Determining whether inflammation, mitochondrial dysfunction, or nutrient sensing is the primary driver</p>
              </div>
            </li>
            
            <li className="flex">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-blue-800 font-medium">3</span>
              </div>
              <div>
                <p className="font-medium text-blue-900">Tailored interventions addressing specific mechanisms</p>
                <p className="text-sm text-blue-800">Matching therapeutic protocols to individual patient pathophysiology</p>
              </div>
            </li>
            
            <li className="flex">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-blue-800 font-medium">4</span>
              </div>
              <div>
                <p className="font-medium text-blue-900">Multimodal approaches addressing interconnected pathways</p>
                <p className="text-sm text-blue-800">Recognizing that multiple interventions are typically needed for optimal results</p>
              </div>
            </li>
            
            <li className="flex">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-blue-800 font-medium">5</span>
              </div>
              <div>
                <p className="font-medium text-blue-900">Regular monitoring and protocol adjustment</p>
                <p className="text-sm text-blue-800">Based on biomarker response and clinical progress</p>
              </div>
            </li>
          </ol>
        </div>
        
        <p className="mb-4 text-center italic">
          By viewing insulin resistance through this systems biology lens, clinicians can develop more effective, personalized interventions that not only address metabolic dysfunction but also potentially modify the aging trajectory.
        </p>
        
        <div className="flex justify-center">
          <div className="bg-gray-100 px-4 py-2 rounded-lg inline-block">
            <p className="text-sm text-gray-600">For clinical resources related to this lecture, including protocols and assessment tools, see the Resources tab.</p>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 12: References
  {
    title: "References",
    content: (
      <div className="prose prose-sm max-w-none">
        <h3>Primary References</h3>
        <ol className="space-y-1">
          <li>Petersen MC, Shulman GI. Mechanisms of Insulin Action and Insulin Resistance. Physiol Rev. 2018;98(4):2133-2223.</li>
          <li>Fazakerley DJ, Krycer JR, Kearney AL, et al. Muscle and adipose tissue insulin resistance: malady without mechanism? J Lipid Res. 2019;60(10):1720-1732.</li>
          <li>Roden M, Shulman GI. The integrative biology of type 2 diabetes. Nature. 2019;576(7785):51-60.</li>
          <li>Jais A, Brüning JC. Hypothalamic inflammation in obesity and metabolic disease. J Clin Invest. 2017;127(1):24-32.</li>
          <li>Hotamisligil GS. Inflammation, metaflammation and immunometabolic disorders. Nature. 2017;542(7640):177-185.</li>
        </ol>
        
        <h3>Recent Clinical Trials and Reviews</h3>
        <ol className="space-y-1" start={6}>
          <li>Rogacka D, Audzeyenka I, Rychlowski M, et al. Metformin overcomes high glucose-induced insulin resistance of podocytes by pleiotropic effects on SIRT1 and AMPK. Biochim Biophys Acta Mol Basis Dis. 2021;1867(10):166158.</li>
          <li>Wilkinson MJ, Manoogian ENC, Zadourian A, et al. Ten-Hour Time-Restricted Eating Reduces Weight, Blood Pressure, and Atherogenic Lipids in Patients with Metabolic Syndrome. Cell Metab. 2023;31(1):92-104.e5.</li>
          <li>Xia Y, Sneyd HJ, McConnell KN, et al. Aging, longevity, and insulin resistance. Int Rev Neurobiol. 2020;155:177-208.</li>
          <li>Smith RL, Soeters MR, Wüst RCI, Houtkooper RH. Metabolic Flexibility as an Adaptation to Energy Resources and Requirements in Health and Disease. Endocr Rev. 2019;39(4):489-517.</li>
          <li>Song P, Hoang K, Ahmed A, et al. Targeting redox signaling for diabetic vascular complications: Promises, realities, and challenges. Free Radic Biol Med. 2021;162:174-187.</li>
        </ol>
      </div>
    )
  }
];

export default function InsulinResistance() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship">Mentorship</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship/longevity">Longevity</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <span className="text-gray-500">Insulin Resistance</span>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="md:col-span-2 space-y-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
                Insulin Resistance: Molecular Pathways & Clinical Interventions
              </h1>
              <p className="text-[#7F8C8D] mb-6">
                Understanding insulin resistance as a systems-level disorder with diverse mechanistic drivers and clinical implications
                for longevity medicine practice.
              </p>
              <div className="flex items-center gap-3 mb-8">
                <div className="flex items-center text-sm text-[#7F8C8D]">
                  <FileText className="h-4 w-4 mr-1" />
                  <span>Lecture Duration: 45 minutes</span>
                </div>
                <div className="w-1 h-1 rounded-full bg-gray-300"></div>
                <div className="flex items-center text-sm text-[#7F8C8D]">
                  <Book className="h-4 w-4 mr-1" />
                  <span>Last Updated: April 20, 2025</span>
                </div>
              </div>
            </div>
            
            <Separator />
            
            {/* Slideshow */}
            <Slideshow slides={lectureSlides} />
            
            {/* Video placeholder */}
            <VideoPlaceholder />
          </div>
          
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-20">
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold mb-4">Key Takeaways</h3>
                  <ul className="space-y-3">
                    <li className="flex">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <p className="text-sm">Insulin resistance is a systems-level disorder involving multiple mechanisms beyond glucose uptake</p>
                    </li>
                    <li className="flex">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <p className="text-sm">Mitochondrial dysfunction, inflammation, and nutrient sensing pathway dysregulation are key contributors</p>
                    </li>
                    <li className="flex">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <p className="text-sm">Aging and insulin resistance have bidirectional relationships, each accelerating the other</p>
                    </li>
                    <li className="flex">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <p className="text-sm">Multimodal interventions targeting specific mechanisms offer more effective clinical outcomes</p>
                    </li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card className="mt-6">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold mb-4">Resources</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Clinical Protocols</h4>
                      <ul className="space-y-1">
                        <li className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                          <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                          <span>Mitochondrial Support Protocol</span>
                        </li>
                        <li className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                          <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                          <span>Anti-Inflammatory Intervention Protocol</span>
                        </li>
                        <li className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                          <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                          <span>Lab Ordering and Interpretation Guide</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Research Updates</h4>
                      <ul className="space-y-1">
                        <li className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                          <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                          <span>Latest Clinical Trials Summary</span>
                        </li>
                        <li className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                          <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                          <span>Insulin Resistance and Longevity Review</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="mt-6">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold mb-4">Navigation</h3>
                  <div className="flex flex-col space-y-2">
                    <Link href="/mentorship/lectures/what-is-longevity-medicine" className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                      <ArrowLeft className="h-4 w-4 mr-1" /> Previous: What Is Longevity Medicine?
                    </Link>
                    <Link href="/mentorship/lectures/questioning-gold-standard" className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                      Next: Questioning the Gold Standard <ArrowRight className="h-4 w-4 ml-1" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}